import moment from 'moment';
import { get } from 'lodash';
import { formatAmountForLocale } from 'Utils/currency-config-for-locale/currencyConfigForLocale';
import { formatApiDate, timestampToApiDate } from 'Utils/date/apiDate';
const API_DATE_FORMAT = 'YYYYMMDD';
export type ChildrenProps = ReadonlyArray<{
  readonly id: string;
  readonly birthdate: string;
  readonly customerId?: string;
  readonly firstName?: string;
  readonly lastName?: string;
}>;
export type ChildcareType = Readonly<UpsellChildcare['productInfo']>;

function getCtaTypeChildcare(childcare: UpsellChildcare): {
  type: 'add' | 'disabled' | 'none' | 'remove' | 'full';
  tracking?: 'addToCart' | 'removeFromCart';
} {
  const { childId } = childcare.pricingInfo;
  const currentChildData = childcare.pricingInfo.attendees?.find(
    (attendee) => attendee.id === childId,
  );
  const currentChildHasRentedChildcare =
    currentChildData?.rentedAmount && currentChildData.rentedAmount.value > 0;

  if (currentChildHasRentedChildcare) {
    return {
      type: 'remove',
      tracking: 'removeFromCart',
    };
  }

  if (childcare.pricingInfo.isFullyPaid) {
    return {
      type: 'none',
    };
  }

  const currentChildInfos = childcare.pricingInfo.attendees?.find(
    (attendee) => attendee.id === childId,
  );
  // the mini clubmed is always included
  const isChildcarePetitOrBaby = childcare.productInfo.ageInMonths.min === 4 || 24;
  const allScheduleSlotsAreFullOrPaid = currentChildInfos?.schedules?.every(({ timeSlots }) =>
    timeSlots.every(({ remainingStock, isPaid }) => remainingStock === 0 || isPaid),
  );
  const childcareIsFull = isChildcarePetitOrBaby && allScheduleSlotsAreFullOrPaid;

  if (childcareIsFull) {
    return {
      type: 'full',
    };
  }

  return {
    type: 'add',
    tracking: 'addToCart',
  };
}

function getFormattedPricePerDay(price: number, tripDuration: number, locale: string) {
  const pricePerDay = price / tripDuration;
  return formatAmountForLocale(pricePerDay, locale);
}

function isChildAgedXOrLess(birthdate: string, targetAge: number) {
  return moment(birthdate, API_DATE_FORMAT).isAfter(
    moment().startOf('day').subtract(targetAge, 'years'),
  );
}

function hasChildrenAgedXOrLess(
  attendees: ReadonlyArray<{
    readonly birthdate: string;
  }>,
  targetAge: number,
) {
  return (
    attendees.filter((attendee) => isChildAgedXOrLess(attendee.birthdate, targetAge)).length > 0
  );
}

function getCurrentChild(items: ChildrenProps, itemId: string) {
  return items.find(({ id }) => id === itemId);
}

function getCurrentKid(items: Array<any>, currentChild: any): Kid {
  return items.find((item) => {
    if (item.id) {
      return (
        timestampToApiDate(item.birthdate) === currentChild.birthdate && item.id === currentChild.id
      );
    }

    if (item.neolid && currentChild.customerId) {
      return (
        timestampToApiDate(item.birthdate) === currentChild.birthdate &&
        item.neolid === currentChild.customerId
      );
    }

    return timestampToApiDate(item.birthdate) === currentChild.birthdate;
  });
}

function getFormatDate(item: any, message: string) {
  return formatApiDate(get(item, 'birthdate', ''), message);
}

const includedAndNotIncludedChildcares = (childcareList: ReadonlyArray<UpsellChildcare>) =>
  childcareList.reduce(
    (acc, childcare) => {
      if (childcare.productInfo.isIncluded) {
        acc.includedChildcares.push(childcare);
      } else {
        acc.notIncludedChildcares.push(childcare);
      }

      return acc;
    },
    {
      includedChildcares: [],
      notIncludedChildcares: [],
    },
  );

const getOverloadChildcareDataForChalet = ({
  proposals,
  currentChildcareId,
  currentChildId,
}: {
  proposals: ReadonlyArray<Proposal>;
  currentChildcareId: string;
  currentChildId: string;
}) => {
  if (
    proposals.every(({ additionalServices }) =>
      additionalServices.childcares.some(({ pricingInfo }) =>
        pricingInfo.schedules.some(({ slots }) => {
          const r = slots.some(
            ({ attendees, id }) =>
              id === currentChildcareId &&
              attendees.some(({ id: idChild }) => idChild === currentChildId),
          );
          return r;
        }),
      ),
    )
  ) {
    return;
  }

  const proposalsWithDifferentChildcare = proposals.filter(
    ({ additionalServices }) =>
      !additionalServices.childcares.some(({ pricingInfo }) =>
        pricingInfo.schedules.some(({ slots }) => {
          const r = slots.some(
            ({ attendees, id }) =>
              id === currentChildcareId &&
              attendees.some(({ id: idChild }) => idChild === currentChildId),
          );
          return r;
        }),
      ),
  );
  const payload = {
    proposalIds: proposalsWithDifferentChildcare.map<string>(({ id }) => id),
    serviceIds: [
      proposalsWithDifferentChildcare[0].additionalServices.childcares.find(({ pricingInfo }) =>
        pricingInfo.attendees?.some(({ id }) => id === currentChildId),
      )?.pricingInfo.schedules[0].slots[0].id || '',
    ],
  };
  return payload;
};

export {
  getCtaTypeChildcare,
  includedAndNotIncludedChildcares,
  getFormattedPricePerDay,
  hasChildrenAgedXOrLess,
  getCurrentChild,
  getCurrentKid,
  getFormatDate,
  getOverloadChildcareDataForChalet,
};
